// R113 #7/#9 (Jesse: "when I delete a section of the property details sheet
// template it just stays sitting there in the same place; I want those to
// filter down to the bottom" / "move 🗄 Template deleted to the very bottom"):
// ONE ordering rule for the master-template sheet AND every property sheet.
// Pure, shared by the client (ESM import) and `node lib/pdsOrder.test.mjs`.
//
//   orderSections(rows, savedOrder, opts)
//     rows        [{ key, removed? , …}] in their default (schema) order
//     savedOrder  app_settings 'ob_section_order' — [keys]; may be empty
//     opts.park   the park section's key (default 'custom_template_deleted')
//
// Result, in this order:
//   1. ACTIVE rows, stable-sorted by savedOrder (keys the saved order doesn't
//      know keep their default position after the ordered ones — the R62
//      rule, so a new schema section is never hidden by a stale order);
//   2. REMOVED rows (removed:true), same stable sort among themselves;
//   3. the PARK row last of all, removed or not.
// The input array is never mutated. Nothing here touches the server: the
// saved order itself is unchanged; the sheets just stop honouring a removed
// key's old slot (and ↑↓ only ever post the active block — see the pages).
export const PARK_SECTION_KEY = 'custom_template_deleted';

export function orderSections(rows, savedOrder, opts = {}) {
  const park = (opts && opts.park) || PARK_SECTION_KEY;
  const list = Array.isArray(rows) ? rows.filter(r => r && typeof r === 'object' && r.key != null) : [];
  const order = Array.isArray(savedOrder) ? savedOrder : [];
  const pos = new Map();
  order.forEach((k, i) => { if (!pos.has(k)) pos.set(k, i); });
  const rank = (r) => (pos.has(r.key) ? pos.get(r.key) : Number.MAX_SAFE_INTEGER);
  // Array.prototype.sort is stable in every engine we ship to (ES2019+), so
  // equal ranks keep the input order.
  const sorted = [...list].sort((a, b) => rank(a) - rank(b));
  const active = sorted.filter(r => !r.removed && r.key !== park);
  const removed = sorted.filter(r => !!r.removed && r.key !== park);
  const parked = sorted.filter(r => r.key === park);
  return [...active, ...removed, ...parked];
}

// The keys ↑↓ may rearrange: the active block only (no removed rows, never
// the park row) — what the move handlers swap in and POST, prefixed by
// nothing and followed by the untouched removed keys + park (so a saved
// order can never re-pin a removed key above an active one).
export function movableKeys(rows, savedOrder, opts = {}) {
  const park = (opts && opts.park) || PARK_SECTION_KEY;
  return orderSections(rows, savedOrder, opts).filter(r => !r.removed && r.key !== park).map(r => r.key);
}

// The full list to persist after an ↑↓ swap of the active block.
export function orderToSave(rows, savedOrder, activeKeys, opts = {}) {
  const park = (opts && opts.park) || PARK_SECTION_KEY;
  const all = orderSections(rows, savedOrder, opts);
  const removed = all.filter(r => !!r.removed && r.key !== park).map(r => r.key);
  const hasPark = all.some(r => r.key === park);
  const seen = new Set();
  const out = [];
  for (const k of [...activeKeys, ...removed, ...(hasPark ? [park] : [])]) { if (!seen.has(k)) { seen.add(k); out.push(k); } }
  return out;
}
